import CONFIG from '@/config';
import React, { useEffect, useState } from 'react';

import { formDataInstance } from '@/common/axios';
import { getRestHeaders } from '@/common/utils/api';
import { StoreStaff, useActiveStoreStaff } from '@/hooks/useActiveStoreStaff';
import { useSnackbar } from 'notistack';

// Maps to a purchasable_item
export interface R2LineItem {
  purchasable_item_id: number;
  purchase_category_id: number;
  purchase_category_description: string;
  appointment_id: number;
  description: string;
  callback: string;
  quantity: number;
  final_amount: number;
  next_bill_price: number; // for memeberships with an initial charge in the future
  dollar_discount: number;
  percentage_discount: number;
  manual_discount_reason: number | null;
  manual_discount_reason_other: string | null;
  member_price: number | null;
  non_member_price: number;
  is_taxable: boolean;
  credit_mask: number;
  credit_cost: number;
  usable_credits: R2LineItemUsableCredits[];
  is_unknown: boolean;
  sales_rep_id: number;
  sales_rep_name: string;
  chosen_credit_type: number;
  chosen_credit_store: number;
  guid: string;
  parent_guid: string | null;
  promo_code_savings: number;
  promo_price_override: number;
  promo_extra_qantity?: number;
  promo_credits_given: number;
  old_drip_ingredients: R2OldDripIngredient[];
  children_guids: string[];
  is_ingredient: boolean;
  price_description: string;
  commission_auth_user: number;
  commission_auth_user_name: string;

  // todo
  appointment?: number;
  autoSelectedCredit?: number;
  creditMasksSupported?: number;
  creditMask?: number;
  creditCost?: number;
  creditsUsed?: number;
  tip_recipient: number; // deprecated
  tip_staff_recipient: number;
  tip_amount: number;
  tip_recipient_name: string; // deprecated
  tip_staff_recipient_name: string;
  tip_percentage_amount: number;

  rolls_into_membership_id: number;
  rolls_into_membership_description: number;
  founder_discount: number;
  is_founder: boolean;
  bill_on_store_open: boolean;
  payment_method_id: number;
  payment_method_description: number;
  next_bill_date: string;
  is_rolls_into_membership: boolean;
  ingredient_price_count: number;
  ingredient_id?: number;
  purchase_category_override?: number;
  is_taxable_override?: boolean;
  validation_error: string;
  coupon_code_id: number;
}

export interface R2AvailablePurchaseCategory {
  id: number;
  description: string;
}

interface R2OldDripIngredient {
  shortDescription: string;
  id: string;
  priceCount: boolean;
}

export interface R2LineItemUsableCredits {
  count: number;
  credit_mask: number;
  credit_type: number;
  credit_value: number;
  description: string;
  expires_first: number;
  total_value: number;
  purchase_store_id: number;
  purchase_store_code: string;
}

// Tip applied to a therapist
interface R2TherapistTip {
  amount: number;
  therapist_id: number;
  therapist_name: string;
  percentage_amount: number;
  guid: string;
}

// Gift cards being bought
export interface R2BoughtGiftCard {
  initial_balance: number;
  gift_card_number: string;
  dollar_discount: number;
  percentage_discount: number;
  manual_discount_reason: number | null;
  manual_discount_reason_other: string | null;
  sales_rep_id: number;
  sales_rep_name: string;
  guid: string;
  granted_by_promo_id: number;
  granted_by_line_item: string;
  coupon_code_id: number;
  promo_description?: string;
  final_amount: number;
  validation_error: string;
  commission_auth_user: number;
  commission_auth_user_name: string;
}

// Payment line which will be turned into a purchased payment method
interface R2PaymentLine {
  amount: number;
  payment_method_id: number;
  stripe_payment_intent: string;
  stripe_captured_charge: string;
  failed: boolean;
  method: string;
  description: string;
  is_account_balance: boolean;
  is_cash: boolean;
  cash_taken: number;
  gift_card_number: string;
  apply_remaining_blance?: boolean;
  third_party_provider: string;
  third_party_code: string;
  paying_customer_id: number;
  extra_credit_count: number;
  extra_credit_type?: number;
  num_dials_used: number;
  is_processed: boolean;
  processing_error: string;
  guid: string;
}

// Credit
interface R2Credit {
  description: string;
  customer_credit_id: number;
  membership_type_id?: number;
  purchase_store_code?: string;
  is_usable_by_studio?: boolean;
  dial_value?: number;
  mask: number;
  type: number;
  value: number;
  expires: number;
  used_on: string;
  from_promo: boolean;
  awarded_by: string;
}

interface UsedGiftCards {
  id: number;
  gift_card_no: string;
  remaining_balance: number;
}

export interface R2AvailableCoupon {
  code: string;
  description: string;
  percentage: number;
  id: number;
  purchase_categories: number[];
}

interface CreditSummary {
  description: string;
  used: number;
  remaining: number;
  new: number;
  promo: boolean;
  store_code: string;
}

interface oldCustomerMemberships {
  description: string;
  is_frozen?: boolean;
  isFrozen?: boolean;
}

export interface NewMembership {
  purchasable_item_id: number;
  description: string;
  is_founder: boolean;
  line_item: string;
  monthly_price: number;
  next_bill_date: string;
  rolls_into_per_month_price: number;
  rolls_into_membership_id: number;
  customer_membership_id: number;
  // Recomputed by the backend on every pending-ticket fetch. Optional because tickets
  // built before the backend started sending it will omit it entirely.
  requires_agreement?: boolean;
}

export interface R2PendingTicket {
  id: number;
  base64id: string;
  store_id: number;
  store_code: string;
  store_pricing_tier: number;
  store_tax_rate: number;
  store_is_open: boolean;
  line_items: R2LineItem[];
  therapist_tips: R2TherapistTip[];
  new_memberships: NewMembership[];
  gift_cards_being_purchased: R2BoughtGiftCard[];
  credits_old: R2Credit[];
  credits_new: R2Credit[];
  promo_code_id: number;
  promo_code_code: string;
  promo_code_savings: number;
  promo_total_credits: number;
  promo_code_description: string;
  promo_code_percentage: number;
  promo_code_callback_data: any;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_guid: string;
  customer_id: number;
  customer_id_base64: string;
  customer_is_member: boolean;
  customer_is_frozen: boolean;
  customer_account_balance: number;
  applied_account_balance: number;
  customer_default_payment_id: number;
  customer_default_payment_last4: number;
  customer_default_payment_card_issuer: string;
  customer_last_promo_used_id: number;
  num_credits_used: number;
  subtotal: number;
  tax_applied: number;
  remaining_balance: number;
  total: number;
  membership_savings: number;
  created_at: number;
  updated_at: number;
  payment_lines: R2PaymentLine[];
  status: string;
  email_error_message: string;
  used_gift_cards: UsedGiftCards[];
  old_customer_memberships: oldCustomerMemberships[];
  credit_summary: CreditSummary[];
  available_coupons: R2AvailableCoupon[];
  ingredients_limit: { [callback: string]: number };
  default_rolls_into_membership_id?: number;
  idempotency_key: string;
}

export interface R2AvailableItem {
  description: string;
  id: number;
  purchase_category_description: string;
  purchase_category_id: number;
  callback: string;
  callback_data: string;
  founder_discount: number;
  is_membership: boolean;
}

interface PendingTicketRequestOptions {
  variables?: {
    id?: string;
  };
}

// we should consider moving all error responses to provide user feedback via a consistent means
const shouldPopToastForError = (msg: string) => {
  if (
    msg ===
    'One or more line items do not have a Staff Member selected for sales attribution.'
  ) {
    return true;
  }
  return false;
};

export const Retail2Context = React.createContext<{
  loadingTicket: boolean;
  isLoading2: boolean;
  deleteItemGuid: string;
  editItemGuid: string;
  ticketApiResponse: any;
  addConvertedMembership: (vars: any) => void;
  callTicketApi: (vars: any, endpoint: string, setTicket: boolean) => any;
  setEditItemGuid: (guid: string) => void;
  setDeleteItemGuid: (guid: string) => void;
  findItemByGuid: any;
  setErrorMessage: (m: string) => void;
  errorMessage: string;
  warningMessage: string;
  setWarningMessage: (string) => void;
  loadPendingTicket2: (options?: PendingTicketRequestOptions) => void;
  modifyCurrentTicket: (vars: any, endpiont: string) => Promise<any>;
  isAddItemDialogLoading: boolean;
  setIsAddItemDialogLoading: (isLoading: boolean) => void;
  showAddItemModal: boolean;
  setShowAddItemModal: (show: boolean) => void;
  showPurchasedGiftCardModal: boolean;
  setShowPurchasedGiftCardModal: (show: boolean) => void;
  showUnKnownItemModel: boolean;
  setShowUnKnownItemModal: (show: boolean) => void;
  purchasableItemIndexToEdit: number;
  setPurchasableItemIndexToEdit: (p: number) => void;
  purchasedGiftCardIndexToEdit: string;
  setPurchasedGiftCardIndexToEdit: (p: string) => void;
  addTip: boolean;
  setAddTip: (_add: boolean) => void;
  showPaymentModal: boolean;
  setShowPaymentModal: (show: boolean) => void;
  remainingAmount: number;
  pendingTicketId: string;
  setPendingTicketId: (id: string) => void;
  pendingTicket2: R2PendingTicket;
  lastPendingTicket2: R2PendingTicket;
  showErrorModal: boolean;
  setShowErrorModal: (show: boolean) => void;
  errors: string[];
  setErrors: (errors: string[]) => void;
  successModalObject: SuccessModalProps;
  setSuccessModalObject: (obj: SuccessModalProps) => void;
  showSuccessModal: boolean;
  setShowSuccessModal: (show: boolean) => void;
  selectedPurchaseCategory: string;
  setSelectedPurchaseCategory: (category: string) => void;
  // new modal
  showCreditCardModal: boolean;
  setShowCreditCardModal: (show: boolean) => void;
  showNewCashModal: boolean;
  setShowNewCashModal: (show: boolean) => void;
  showNewSwipeModal: boolean;
  setShowNewSwipeModal: (show: boolean) => void;
  showErrorCard: boolean;
  setShowErrorCard: (show: boolean) => void;
  cardErrors: string[];
  setCardErrors: (errors: string[]) => void;
  showNewCouponModal: boolean;
  setShowNewCouponModal: (show: boolean) => void;
  showNewGiftCardModal: boolean;
  setShowNewGiftCardModal: (show: boolean) => void;
  showNewSomeOneElseModal: boolean;
  setShowNewSomeOneElseModal: (show: boolean) => void;
  sendConfirmEmail: boolean;
  setSendConfirmEmail: (show: boolean) => void;
  addTipModalOpen: boolean;
  setAddTipModalOpen: (s: boolean) => void;
  unknownItemModalOpen: boolean;
  setUnknownItemModalOpen: (s: boolean) => void;
  addGiftCardModalOpen: boolean;
  setAddGiftCardModalOpen: (s: boolean) => void;
  therapist: any;
  setTherapist: (t: any) => void;
  addDiscountModalOpen: boolean;
  setAddDiscountModalOpen: (s: boolean) => void;
  tipAmount: number;
  setTipAmount: (t: number) => void;
  availableItems: R2AvailableItem[];
  availableCategories: R2AvailablePurchaseCategory[];
  productCategories: R2AvailablePurchaseCategory[];
  accountBalanceAmountModaOpen: boolean;
  setAccountBalanceAmountModaOpen: (b: boolean) => void;
  giftCardBalanceAmountModalOpen: boolean;
  setGiftCardBalanceAmountModalOpen: (b: boolean) => void;
  dialsAmountModalOpen: boolean;
  setDialsAmountModalOpen: (s: boolean) => void;
  showMembershipModal: boolean;
  setShowMembershipModal: (s: boolean) => void;
  showPlanModal: boolean;
  setShowPlanModal: (s: boolean) => void;
  storeStaff: StoreStaff[];
  loadingStaff: boolean;
  setPendingTicket2: (ticket: R2PendingTicket) => void;
}>(null);

export const Retail2ContextProvider: React.FC = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  /* useState */
  const [ticketApiResponse, setTicketApiResponse] = useState(null);
  const [deleteItemGuid, setDeleteItemGuid] = useState(null);
  const [editItemGuid, setEditItemGuid] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [isAddItemDialogLoading, setIsAddItemDialogLoading] = useState(false);
  const [pendingTicket2, setPendingTicket2] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [lastPendingTicket2, setLastPendinTicket2] = useState(null);
  const [purchasableItemIndexToEdit, setPurchasableItemIndexToEdit] =
    useState(null);
  const [pendingTicketId, setPendingTicketId] = useState<string>(null);
  const [addTip, setAddTip] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showErrorCard, setShowErrorCard] = useState(false);
  const [cardErrors, setCardErrors] = useState<string[]>([]);
  const [remainingAmount, setRemainingAmount] = useState(null);
  const [showPurchasedGiftCardModal, setShowPurchasedGiftCardModal] =
    useState(false);
  const [showUnKnownItemModel, setShowUnKnownItemModal] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [warningMessage, setWarningMessage] = useState<string>(null);
  const [successModalObject, setSuccessModalObject] =
    useState<SuccessModalProps>(null);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [selectedPurchaseCategory, setSelectedPurchaseCategory] =
    useState<string>(null);
  const [purchasedGiftCardIndexToEdit, setPurchasedGiftCardIndexToEdit] =
    useState<string>(null);

  // *************************************** new modals
  const [showCreditCardModal, setShowCreditCardModal] = useState(false);
  const [showNewCashModal, setShowNewCashModal] = useState(false);
  const [showNewSwipeModal, setShowNewSwipeModal] = useState(false);
  const [showNewCouponModal, setShowNewCouponModal] = useState(false);
  const [showNewGiftCardModal, setShowNewGiftCardModal] = useState(false);
  const [showNewSomeOneElseModal, setShowNewSomeOneElseModal] = useState(false);
  const [sendConfirmEmail, setSendConfirmEmail] = useState(true);
  const [addDiscountModalOpen, setAddDiscountModalOpen] = useState(false);
  const [addTipModalOpen, setAddTipModalOpen] = useState(false);
  const [unknownItemModalOpen, setUnknownItemModalOpen] = useState(false);
  const [addGiftCardModalOpen, setAddGiftCardModalOpen] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [therapist, setTherapist] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [availableItems, setAvailableItems] = useState<R2AvailableItem[]>([]);
  const [availableCategories, setAvailableCategories] = useState<
    R2AvailablePurchaseCategory[]
  >([]);
  const [productCategories, setProductCategories] = useState<
    R2AvailablePurchaseCategory[]
  >([]);

  const { items: storeStaff, loading: loadingStaff } = useActiveStoreStaff();

  useEffect(() => {
    const getAvailableItems = async () => {
      const mapCategories = (
        availableItems: R2AvailableItem[]
      ): Array<{ id: number; description: string }> => {
        return Array.from(
          new Map(
            availableItems.map(i => [
              i?.purchase_category_id,
              i?.purchase_category_description
            ])
          ),
          ([id, description]) => ({
            id: Number(id),
            description
          })
        );
      };

      const res = await formDataInstance.get(
        `${CONFIG.FINANCE_API_URL}/staff/available-items`,
        {
          headers: getRestHeaders()
        }
      );

      if (res.status === 200) {
        const items: R2AvailableItem[] = res.data.data.available_items;
        setAvailableItems(items);
        setAvailableCategories(mapCategories(items));
      }
    };
    getAvailableItems();

    const getProductCategories = async () => {
      const res = await formDataInstance.get(
        CONFIG.API + '/pos_product/get_product_categories',
        { params: { include_misc: 1 } }
      );
      if (res.data?.status === 'success' && res.data?.data?.categories) {
        const categories: R2AvailablePurchaseCategory[] =
          res.data?.data?.categories;

        // remove Memberships and IV Ingredients categories
        const categories_to_remove = [110, 280];
        const filtered_categories = categories.filter(
          cat => !categories_to_remove.includes(cat.id)
        );

        setProductCategories(filtered_categories);
      }
    };
    getProductCategories();
  }, []);
  // *************************************** new modals
  const [accountBalanceAmountModaOpen, setAccountBalanceAmountModaOpen] =
    useState(false);
  const [giftCardBalanceAmountModalOpen, setGiftCardBalanceAmountModalOpen] =
    useState(false);
  const [dialsAmountModalOpen, setDialsAmountModalOpen] = useState(false);
  // new modal

  // Wrapper function to call into the backend
  // If setTicket=true, overwrite penidngTicket2 with the 200 json response
  async function callTicketApi(vars, endpoint, setTicket) {
    setIsLoading2(true);
    setErrorMessage(null);
    setWarningMessage(null);

    const postVars = {
      ...vars
    };

    // Unless we are requesting a new ticket, use our existing ticket if we have one
    if (!('pending_ticket' in vars) && pendingTicket2) {
      vars['pending_ticket'] = pendingTicket2?.id;
    }

    // Construct our full endpiont
    const url = CONFIG.API + '/pos_retail2/' + endpoint + '/';

    // Call to the backend REST API and handle success and failures using the proimises
    const processResponseData = r => {
      setTicketApiResponse(r);
      if (setTicket) {
        if (pendingTicket2) {
          setLastPendinTicket2(pendingTicket2);
        } else {
          setLastPendinTicket2(r);
        }
        const items = sortLineItems(r?.line_items || []);
        r.line_items = items;
        setPendingTicket2(r);
        setRemainingAmount(r?.remaining_balance ?? null);
      }
    };

    // Call to the backend REST API and handle success and failures using the proimises
    const pendingTicket = await formDataInstance
      .post(url, postVars, {
        headers: getRestHeaders()
      })
      .then(res => {
        const r = res?.data?.data;
        processResponseData(r);
        return r;
      })
      .catch(e => {
        // Server had a non-200 response
        const msg = e?.response?.data?.error || '';
        const warning_message = e?.response?.data?.warning || '';
        const data = e?.response?.data?.data;
        if (data) {
          processResponseData(data);
        }
        if (shouldPopToastForError(msg)) {
          enqueueSnackbar(msg, {
            variant: 'error'
          });
        } else {
          setErrorMessage(msg || null);
          setWarningMessage(warning_message || null);
        }
        console.error(msg);
        return msg;
      });

    setIsLoading2(false);
    return pendingTicket;
  }

  // get_pending_ticket
  const loadPendingTicket2 = async vars => {
    const id = vars?.variables.id;
    const postVars = {
      pending_ticket: id
    };

    return callTicketApi(postVars, 'get_pending_ticket', true);
  };

  // get_pending_ticket
  const addConvertedMembership = async vars => {
    const postVars = {
      ...vars
    };

    return callTicketApi(postVars, 'add_converted_membership', true);
  };

  // Trying to convert a customer to a new memberhsip. See if a ticket exists, i
  const modifyCurrentTicket = async (vars, endpoint) => {
    setShowPaymentModal(false);

    const postVars = {
      ...vars,
      pending_ticket: pendingTicket2?.id
    };

    return await callTicketApi(postVars, endpoint, true);
  };

  /* useEffects */
  const loadingTicket = isLoading2 || loadingStaff;

  useEffect(() => {
    setAddTip(false);
    setPurchasableItemIndexToEdit(null);
    setShowPaymentModal(false);
  }, [loadingTicket]);

  return (
    <Retail2Context.Provider
      value={{
        sendConfirmEmail,
        setSendConfirmEmail,
        showNewSomeOneElseModal,
        setShowNewSomeOneElseModal,
        showNewGiftCardModal,
        setShowNewGiftCardModal,
        showNewCouponModal,
        setShowNewCouponModal,
        showNewSwipeModal,
        setShowNewSwipeModal,
        showNewCashModal,
        setShowNewCashModal,
        showCreditCardModal,
        setShowCreditCardModal,
        callTicketApi,
        addConvertedMembership,
        ticketApiResponse,
        findItemByGuid,
        setErrorMessage,
        errorMessage,
        warningMessage,
        setWarningMessage,
        loadPendingTicket2,
        isLoading2,
        editItemGuid,
        setEditItemGuid,
        deleteItemGuid,
        isAddItemDialogLoading,
        setIsAddItemDialogLoading,
        setDeleteItemGuid,
        modifyCurrentTicket,
        loadingTicket,
        showAddItemModal,
        setShowAddItemModal,
        purchasableItemIndexToEdit,
        setPurchasableItemIndexToEdit,
        addTip,
        setAddTip,
        showPaymentModal,
        setShowPaymentModal,
        remainingAmount,
        purchasedGiftCardIndexToEdit,
        setPurchasedGiftCardIndexToEdit,
        showPurchasedGiftCardModal,
        setShowPurchasedGiftCardModal,
        showUnKnownItemModel,
        setShowUnKnownItemModal,
        pendingTicketId,
        pendingTicket2,
        lastPendingTicket2,
        setPendingTicketId,
        showErrorModal,
        setShowErrorModal,
        errors,
        setErrors,
        successModalObject,
        setSuccessModalObject,
        showSuccessModal,
        setShowSuccessModal,
        selectedPurchaseCategory,
        setSelectedPurchaseCategory,
        showErrorCard,
        setShowErrorCard,
        cardErrors,
        setCardErrors,
        addTipModalOpen,
        setAddTipModalOpen,
        therapist,
        setTherapist,
        addDiscountModalOpen,
        setAddDiscountModalOpen,
        tipAmount,
        setTipAmount,
        availableItems,
        availableCategories,
        productCategories,
        unknownItemModalOpen,
        setUnknownItemModalOpen,
        addGiftCardModalOpen,
        setAddGiftCardModalOpen,
        accountBalanceAmountModaOpen,
        setAccountBalanceAmountModaOpen,
        giftCardBalanceAmountModalOpen,
        setGiftCardBalanceAmountModalOpen,
        dialsAmountModalOpen,
        setDialsAmountModalOpen,
        showMembershipModal,
        setShowMembershipModal,
        showPlanModal,
        setShowPlanModal,
        storeStaff,
        loadingStaff,
        setPendingTicket2
      }}
    >
      {children}
    </Retail2Context.Provider>
  );
};

export interface SuccessModalMessageProps {
  displayText: string;
  type: 'checked_item' | 'success_reminder';
}
interface SuccessModalProps {
  title: string;
  messages: SuccessModalMessageProps[];
  onClose: () => void;
}

const findItemByGuid = (items: any[], guid: string) => {
  if (guid && items && items.length > 0) {
    return items?.find(o => o?.guid === guid);
  } else {
    return null;
  }
};

const sortLineItems = (lineItems: R2LineItem[]) => {
  const arr = [];
  const objDone = {};

  lineItems?.forEach(li => {
    if (!objDone[li?.guid]) {
      objDone[li?.guid] = true;
      arr.push(li);
      const children = lineItems?.filter(
        l => li?.children_guids?.indexOf(l?.guid) >= 0
      );
      children?.forEach(c => {
        objDone[c?.guid] = true;
        arr.push(c);
      });
    }
  });
  return arr;
};
